// Pure logic behind the prep-item section of the item drawer: the editable
// draft, the checks run before saving, the live cost preview and the
// production arithmetic. No React, no network — covered by
// prepRecipeDraft.test.ts. The server (prep_recipe_store.go) stays the
// authority: it re-checks everything here and alone can see a recipe loop.

import { convertUnits, getCompatibleUnits } from '@/lib/inventory-units'
import type { KitchenPrepItem, PrepIngredientOption, PrepRecipe, PrepRecipePayload, ProductionBatch } from '@/types/kitchenFlowPrep'
import { PREP_TEXT } from './prepStrings'
import { cleanSteps, sameSteps, stepProblems, stepsFromServer, type StepDraft } from '@/lib/recipeCard/methodDraft'

export interface PrepLineDraft {
  key: string
  stock_item_id: string
  qty: string
  unit: string
}

export interface PrepDraft {
  isPrep: boolean
  yieldQty: string
  yieldUnit: string
  lines: PrepLineDraft[]
  /** The numbered method, read on the kitchen's recipe card (migration 148). */
  steps: StepDraft[]
}

let keySeq = 0
export function newPrepLine(over: Partial<PrepLineDraft> = {}): PrepLineDraft {
  keySeq += 1
  return { key: `pl-${keySeq}`, stock_item_id: '', qty: '', unit: '', ...over }
}

const numText = (n: number | null | undefined) => (n == null ? '' : String(n))

/** The draft a saved recipe (or an item with none) starts from. */
export function draftFromRecipe(r: PrepRecipe | undefined, itemUnit: string): PrepDraft {
  if (!r) return { isPrep: false, yieldQty: '', yieldUnit: itemUnit, lines: [], steps: [] }
  return {
    isPrep: r.is_prep,
    yieldQty: numText(r.prep_yield_qty),
    yieldUnit: r.prep_yield_unit || r.unit || itemUnit,
    lines: r.components.map((c) =>
      newPrepLine({ stock_item_id: c.stock_item_id, qty: numText(c.qty), unit: c.unit || c.stock_unit }),
    ),
    steps: stepsFromServer(r.steps),
  }
}

/** Two drafts are the same recipe (ignoring row keys and blank rows). */
export function sameDraft(a: PrepDraft, b: PrepDraft): boolean {
  const norm = (d: PrepDraft) =>
    JSON.stringify({
      isPrep: d.isPrep,
      y: d.isPrep ? [Number(d.yieldQty) || 0, d.yieldUnit] : null,
      l: d.isPrep
        ? d.lines.filter((l) => l.stock_item_id || l.qty.trim()).map((l) => [l.stock_item_id, Number(l.qty) || 0, l.unit])
        : [],
    })
  // The method only exists while the item is a prep item.
  const stepsSame = !a.isPrep && !b.isPrep ? true : sameSteps(a.steps ?? [], b.steps ?? [])
  return norm(a) === norm(b) && stepsSame
}

/** A unit fits when a quantity in it converts to the stock unit. */
export function unitFits(unit: string, stockUnit: string): boolean {
  return unit === stockUnit || getCompatibleUnits(stockUnit).includes(unit)
}

export interface DraftProblem {
  /** Row key, or undefined for the batch-size / whole-recipe problems. */
  key?: string
  message: string
}

/**
 * Problems to fix before saving, in plain words. Empty = ready to save. A
 * draft that is not a prep item is always ready (saving clears the recipe).
 */
export function draftProblems(
  d: PrepDraft,
  itemId: string,
  itemUnit: string,
  options: Record<string, PrepIngredientOption>,
): DraftProblem[] {
  if (!d.isPrep) return []
  const out: DraftProblem[] = []
  const y = Number(d.yieldQty)
  if (d.yieldQty.trim() === '' || !Number.isFinite(y) || y <= 0) out.push({ message: PREP_TEXT.yieldAboveZero })
  else if (!unitFits(d.yieldUnit, itemUnit)) out.push({ message: PREP_TEXT.yieldUnitDoesNotFit(itemUnit) })

  const lines = d.lines.filter((l) => l.stock_item_id || l.qty.trim())
  if (lines.length === 0) out.push({ message: PREP_TEXT.needOneIngredient })
  const seen = new Set<string>()
  for (const l of lines) {
    if (!l.stock_item_id) {
      out.push({ key: l.key, message: PREP_TEXT.pickIngredient })
      continue
    }
    const opt = options[l.stock_item_id]
    if (l.stock_item_id === itemId) out.push({ key: l.key, message: PREP_TEXT.notItself })
    else if (seen.has(l.stock_item_id)) out.push({ key: l.key, message: PREP_TEXT.sameTwice(opt?.name ?? 'This ingredient') })
    seen.add(l.stock_item_id)
    const q = Number(l.qty)
    if (l.qty.trim() === '' || !Number.isFinite(q) || q <= 0) out.push({ key: l.key, message: PREP_TEXT.qtyAboveZero })
    if (opt && l.unit && !unitFits(l.unit, opt.unit)) out.push({ key: l.key, message: PREP_TEXT.unitDoesNotFit(opt.name, opt.unit) })
  }
  for (const message of stepProblems(d.steps ?? [])) out.push({ message })
  return out
}

/** The PUT body. Blank rows are dropped; a draft that is off clears the recipe. */
export function toPayload(d: PrepDraft): PrepRecipePayload {
  if (!d.isPrep) return { is_prep: false, prep_yield_qty: null, prep_yield_unit: '', components: [] }
  return {
    is_prep: true,
    prep_yield_qty: Number(d.yieldQty),
    prep_yield_unit: d.yieldUnit,
    components: d.lines
      .filter((l) => l.stock_item_id)
      .map((l) => ({ stock_item_id: l.stock_item_id, qty: Number(l.qty), unit: l.unit })),
    steps: cleanSteps(d.steps ?? []),
  }
}

export interface CostPreview {
  /** Per row key: the row's cost for one batch, or null when it cannot be priced. */
  lines: Record<string, number | null>
  batchCost: number
  /** Cost of one stock unit of the prep item; null without a usable batch size. */
  perUnit: number | null
  uncosted: number
}

/**
 * Live cost of the draft as typed, before it is saved — the same sum the
 * server's roll-up makes (Σ ingredient qty × rate ÷ batch size) from the
 * picker's rates. The saved figure from the server replaces it on reload.
 */
export function previewCost(d: PrepDraft, itemUnit: string, options: Record<string, PrepIngredientOption>): CostPreview {
  const out: CostPreview = { lines: {}, batchCost: 0, perUnit: null, uncosted: 0 }
  for (const l of d.lines) {
    const opt = options[l.stock_item_id]
    const q = Number(l.qty)
    if (!opt || !(q > 0)) {
      out.lines[l.key] = null
      continue
    }
    const inStock = convertUnits(q, l.unit || opt.unit, opt.unit)
    if (inStock == null || opt.unit_cost == null) {
      out.lines[l.key] = null
      if (inStock != null) out.uncosted += 1
      continue
    }
    const cost = inStock * opt.unit_cost
    out.lines[l.key] = cost
    out.batchCost += cost
  }
  const y = convertUnits(Number(d.yieldQty), d.yieldUnit || itemUnit, itemUnit)
  if (y != null && y > 0) out.perUnit = out.batchCost / y
  return out
}

/** How much one batch makes, in the item's own unit (null = not set / does not convert). */
export function yieldInItemUnit(r: Pick<PrepRecipe, 'prep_yield_qty' | 'prep_yield_unit' | 'unit'>): number | null {
  if (r.prep_yield_qty == null || r.prep_yield_qty <= 0) return null
  const y = convertUnits(r.prep_yield_qty, r.prep_yield_unit || r.unit, r.unit)
  return y != null && y > 0 ? y : null
}

/** A saved recipe a batch can be made from: prep, a batch size and ingredients. */
export function canProduce(r: PrepRecipe | undefined): boolean {
  return Boolean(r?.is_prep && r.components.length > 0 && yieldInItemUnit(r) != null)
}

/**
 * What making `planned` (in the item's unit) takes from the kitchen, per
 * ingredient in its stock unit — the "this will use" list shown before a
 * batch starts. Mirrors the backend's planned ÷ yield × qty.
 */
export function ingredientsForBatch(r: PrepRecipe, planned: number): { stock_item_id: string; name: string; unit: string; qty: number }[] {
  const y = yieldInItemUnit(r)
  if (y == null || !(planned > 0)) return []
  return r.components.map((c) => ({
    stock_item_id: c.stock_item_id,
    name: c.name,
    unit: c.stock_unit,
    qty: Math.round((c.qty_in_stock_unit * planned) / y * 1e5) / 1e5,
  }))
}

/** "0.5 kg less than planned" — the yield variance in words. */
export function yieldVarianceText(b: Pick<ProductionBatch, 'yield_variance_qty' | 'unit'>, fmt: (n: number) => string): string | null {
  const v = b.yield_variance_qty
  if (v == null) return null
  if (Math.abs(v) < 1e-6) return PREP_TEXT.varianceExact
  const q = `${fmt(Math.abs(v))} ${b.unit}`
  return v > 0 ? PREP_TEXT.varianceMore(q) : PREP_TEXT.varianceLess(q)
}

/** A fresh idempotency key for one "Start making" tap (reused on its retry). */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c?.randomUUID) return `pb-${c.randomUUID()}`
  return `pb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * The plain-words part of an API error. The client joins the server's message
 * and its snake_case code ("… — recipe_cycle"); staff should only see the
 * sentence, which the server already writes for them.
 */
export function plainError(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e ?? '')
  return m.replace(/ — [a-z0-9_]+$/, '').trim() || 'Something went wrong. Try again.'
}

/** The kitchen's "this will use" list: planned ÷ one batch × each ingredient. */
export function kitchenIngredientsFor(item: KitchenPrepItem, planned: number): { stock_item_id: string; name: string; unit: string; qty: number }[] {
  if (!(planned > 0) || !(item.batch_qty > 0)) return []
  return item.components.map((c) => ({
    stock_item_id: c.stock_item_id,
    name: c.name,
    unit: c.unit,
    qty: Math.round((c.qty_per_batch * planned) / item.batch_qty * 1e5) / 1e5,
  }))
}
